<template>
  <div class="max-w-5xl mx-auto">
    <v-container>
      <div class="flex items-center justify-between mb-4">
        <div
          class="flex items-center gap-2 cursor-pointer"
          @click="goBack"
        >
          <v-icon>mdi-chevron-left</v-icon>
          <span class="text-sm font-semibold">Back</span>
        </div>
        <v-btn
          icon
          small
          :loading="loadingCoin || loadingHistory"
          title="Refresh balance and history"
          @click="refreshCoin"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>
      <!-- Balance Card -->
      <v-card
        v-if="loadingCoin"
        class="pa-6 mb-6 rounded-xl text-center"
        elevation="1"
      >
        <v-progress-circular indeterminate color="primary" />
      </v-card>
      <v-card
        v-else-if="presentCoinData && presentCoinData.coin"
        class="pa-6 mb-6 rounded-xl"
        elevation="1"
      >
        <div class="flex flex-col md:items-center md:justify-between">
          <div class="w-full">
            <div class="text-lg font-semibold mb-1">
              {{ presentCoinData.coin.name }} Balance
            </div>
            <div class="flex items-center text-2xl font-bold mb-2">
              {{ formatCryptoAmount(presentCoinData.balance) }}
              {{ presentCoinData.coin.symbol }}
              <v-icon small class="ml-2">mdi-eye-outline</v-icon>
            </div>
            <div class="flex space-x-8 mb-4">
              <div class="flex items-center space-x-2 w-full">
                <v-icon color="success" small>mdi-check-circle</v-icon>
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">Available Balance</span>
                  <span class="font-semibold ml-1">
                    {{ formatCryptoAmount(presentCoinData.balance) }}
                  </span>
                </div>
              </div>
              <div
                v-if="hasPendingBalance"
                class="flex items-center space-x-2 w-full"
              >
                <v-icon color="warning" small>mdi-checkbox-blank-circle</v-icon>
                <div class="flex flex-col">
                  <span class="text-xs text-gray-600">Pending Balance</span>
                  <span class="font-semibold ml-1">
                    {{ formatCryptoAmount(presentCoinData.pendingBalance) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <!-- Two per row on phones, one row from md up - five buttons in a
               single flex row overflow a narrow screen. -->
          <div
            class="grid grid-cols-2 gap-2 w-full flex-1 mt-4 md:flex md:gap-4 md:mt-0"
          >
            <v-btn
              color="primary"
              size="lg"
              :disabled="!coinFlags.depositEnabled"
              @click="handleDepositModal('deposit')"
              >Deposit</v-btn
            >
            <v-btn
              color="primary"
              size="lg"
              :disabled="!coinFlags.withdrawEnabled"
              @click="handleDepositModal('withdraw')"
              >Withdraw</v-btn
            >
            <v-btn
              color="primary"
              size="lg"
              :disabled="!coinFlags.tradeEnabled"
              @click="handleDepositModal('swap')"
              >Swap</v-btn
            >
            <v-btn
              color="primary"
              size="lg"
              :disabled="!coinFlags.purchaseEnabled"
              @click="goToTrade('buy')"
              >Buy</v-btn
            >
            <v-btn
              color="primary"
              size="lg"
              :disabled="!coinFlags.saleEnabled"
              @click="goToTrade('sell')"
              >Sell</v-btn
            >
          </div>
        </div>
      </v-card>

      <!-- Tabs -->
      <div class="flex items-center justify-center mb-2">
        <div class="flex w-full md:w-1/2">
          <button
            class="flex-1 py-2 text-base font-medium focus:outline-none"
            :class="
              tab === 'history'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500'
            "
            @click="tab = 'history'"
          >
            History
          </button>
          <button
            class="flex-1 py-2 text-base font-medium focus:outline-none"
            :class="
              tab === 'charts'
                ? 'text-primary border-b-2 border-primary'
                : 'text-gray-500'
            "
            @click="tab = 'charts'"
          >
            Charts
          </button>
        </div>
      </div>
      <v-divider></v-divider>

      <!-- History tab -->
      <div v-if="tab === 'history'" class="mt-4">
        <v-data-table
          :headers="historyHeaders"
          :items="walletHistory"
          :items-per-page="historyPerPage"
          :loading="loadingHistory"
          :server-items-length="historyTotal"
          :footer-props="{ itemsPerPageOptions: [5, 10, 25, 50] }"
          @update:items-per-page="updateHistoryPerPage"
          @update:page="updateHistoryPage"
          @click:row="openHistoryDetails"
        >
          <template v-slot:no-data>
            <div class="py-6 text-center grey--text">
              No transactions yet for this coin.
            </div>
          </template>

          <template v-slot:[`item.createdAt`]="{ item }">
            <span>{{ formatHistoryDate(item.createdAt) }}</span>
          </template>

          <template v-slot:[`item.category`]="{ item }">
            <span class="font-weight-medium">{{ historyLabel(item) }}</span>
          </template>

          <template v-slot:[`item.amount`]="{ item }">
            <span
              :class="item.type === 'credit' ? 'success--text' : 'error--text'"
            >
              {{ item.type === 'credit' ? '+' : '-'
              }}{{ formatCryptoAmount(item.amount) }} {{ coinSymbol }}
            </span>
          </template>
        </v-data-table>
      </div>

      <!-- Charts tab -->
      <div v-if="tab === 'charts'" class="mt-4">
        <div v-if="loadingChart" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <div
          v-else-if="!chartSeries[0] || !chartSeries[0].data.length"
          class="text-center py-8 text-gray-500"
        >
          No chart data available for this coin right now.
        </div>
        <apexchart
          v-else
          type="candlestick"
          height="350"
          :options="chartOptions"
          :series="chartSeries"
        />
      </div>
    </v-container>

    <CryptoAssetModal
      :value="showDepositModal"
      :action-text="activeActionText"
      :modal-type="depositType"
      :data-to-use="depositType === 'crypto' ? 'wallets' : 'assets'"
      @asset-clicked="selectAsset"
      @input="showDepositModal = $event"
    />

    <ServiceUnavailableModal
      :value="showServiceUnavailable"
      :action="restrictedAction"
      @input="showServiceUnavailable = $event"
    />

    <!-- History details -->
    <v-dialog v-model="historyDialog" max-width="480">
      <v-card v-if="selectedHistory">
        <v-card-title class="d-flex align-center justify-space-between">
          <span class="text-capitalize">{{ selectedHistory.category }}</span>
          <v-btn icon small @click="historyDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div
            v-for="row in historyDetailRows"
            :key="row.label"
            class="d-flex justify-space-between py-2 border-b"
          >
            <span class="text-sm text-gray-600">{{ row.label }}</span>
            <span class="text-sm font-medium text-right" :class="row.class">
              {{ row.value }}
            </span>
          </div>
          <div
            v-if="selectedHistory.reference"
            class="d-flex justify-space-between align-center py-2"
          >
            <span class="text-sm text-gray-600">Reference</span>
            <span class="d-flex align-center">
              <span class="text-sm font-medium mr-2">
                {{ selectedHistory.reference }}
              </span>
              <v-btn icon x-small @click="copy(selectedHistory.reference)">
                <v-icon small>mdi-content-copy</v-icon>
              </v-btn>
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import cryptocoin from '../../../../api/users/cryptocoin'
import CryptoAssetModal from '../../../../components/CryptoAssetModal.vue'
import ServiceUnavailableModal from '../../../../components/ServiceUnavailableModal.vue'
import { isTradeRestricted } from '../../../../utils/tradeRestriction'
import VueApexCharts from 'vue-apexcharts'
import {
  formatCryptoAmount,
  getCryptoCategoryLabel
} from '../../../../utils/cryptoHistory'

export default {
  data() {
    return {
      historyPerPage: 10,
      historyPage: 1,
      historyTotal: 0,
      historyHeaders: [
        { text: 'Date', value: 'createdAt' },
        { text: 'Type', value: 'category', sortable: false },
        { text: 'Description', value: 'description', sortable: false },
        { text: 'Amount', value: 'amount', sortable: false }
      ],
      tab: 'history',
      showDepositModal: false,
      depositType: 'crypto',
      searchQuery: '',
      selectedAsset: null,
      wallets: [],
      presentCoinData: {},
      coinId: '',
      loadingCoin: false,
      chart: [],
      loadingChart: false,
      walletHistory: [],
      loadingHistory: false,
      historyDialog: false,
      selectedHistory: null,
      cryptoAssets: [],
      fiatAssets: [
        { symbol: 'USD', name: 'US Dollar' },
        { symbol: 'EUR', name: 'Euro' },
        { symbol: 'GBP', name: 'British Pound' },
        { symbol: 'NGN', name: 'Nigerian Naira' },
        { symbol: 'CAD', name: 'Canadian Dollar' },
        { symbol: 'AUD', name: 'Australian Dollar' }
      ],
      loadingAssets: false,
      // deposit, withdraw, swap
      activeAction: 'deposit',
      showServiceUnavailable: false,
      restrictedAction: 'trade'
    }
  },
  components: {
    CryptoAssetModal,
    ServiceUnavailableModal,
    apexchart: VueApexCharts
  },
  computed: {
    historyDetailRows() {
      const item = this.selectedHistory
      if (!item) return []
      const symbol =
        (item.coin && (item.coin.displaySymbol || item.coin.symbol)) || ''
      const isCredit = item.type === 'credit'
      const rows = [
        {
          label: 'Amount',
          value: `${isCredit ? '+' : '-'}${formatCryptoAmount(
            item.amount
          )} ${symbol}`,
          class: isCredit ? 'success--text' : 'error--text'
        }
      ]
      rows.push({ label: 'Type', value: isCredit ? 'Credit' : 'Debit' })
      if (item.description) {
        rows.push({ label: 'Description', value: item.description })
      }
      rows.push({ label: 'Date', value: this.formatHistoryDate(item.createdAt) })
      return rows
    },
    coinSymbol() {
      const coin = this.presentCoinData && this.presentCoinData.coin
      return coin ? coin.displaySymbol || coin.symbol : ''
    },
    hasPendingBalance() {
      const pending = Number(
        this.presentCoinData && this.presentCoinData.pendingBalance
      )
      return Number.isFinite(pending) && pending > 0
    },
    coinFlags() {
      const coin = this.presentCoinData && this.presentCoinData.coin
      const swapEnabled = coin ? coin.swapEnabled !== false : false
      const saleEnabled = coin ? coin.saleEnabled !== false : false
      return {
        depositEnabled: coin ? coin.depositEnabled !== false : false,
        withdrawEnabled: coin ? coin.withdrawEnabled !== false : false,
        purchaseEnabled: coin ? coin.purchaseEnabled !== false : false,
        swapEnabled,
        saleEnabled,
        // Selling now lives inside Swap, so the button opens for either.
        tradeEnabled: swapEnabled || saleEnabled
      }
    },
    chartSeries() {
      if (!Array.isArray(this.chart) || !this.chart.length) {
        return [{ data: [] }]
      }
      return [
        {
          data: this.chart.map((candle) => ({
            x: new Date(candle.timestamp),
            y: [candle.open, candle.high, candle.low, candle.close]
          }))
        }
      ]
    },
    chartOptions() {
      return {
        chart: {
          type: 'candlestick',
          toolbar: { show: false }
        },
        xaxis: {
          type: 'datetime'
        },
        yaxis: {
          tooltip: { enabled: true }
        }
      }
    },
    filteredAssets() {
      const assets =
        this.depositType === 'crypto' ? this.cryptoAssets : this.fiatAssets
      if (!this.searchQuery) return assets

      return assets.filter(
        (asset) =>
          asset.symbol.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          asset.name.toLowerCase().includes(this.searchQuery.toLowerCase())
      )
    },
    activeActionText() {
      if (this.activeAction === 'deposit') {
        return {
          t1: 'Deposit Crypto',
          t2: 'Deposit Fiat'
        }
      } else if (this.activeAction === 'withdraw') {
        return {
          t1: 'Withdraw Crypto',
          t2: 'Withdraw Fiat'
        }
      } else if (this.activeAction === 'swap') {
        return {
          t1: 'Swap Crypto',
          t2: 'Buy with Naira'
        }
      }

      return {
        t1: 'Deposit Crypto',
        t2: 'Deposit Fiat'
      }
    }
  },
  async mounted() {
    this.coinId = this.$route.params.id
    this.getCoinGraph()
    this.getCryptoAssets()
    // History is scoped to coin + wallet, and the wallet id only exists
    // once the coin detail has loaded.
    await this.getCoinDetail()
    this.getWalletHistory()
  },
  methods: {
    formatHistoryDate(date) {
      if (!date) return ''
      const d = new Date(date)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    },
    goBack() {
      this.$router.go(-1)
    },
    openHistoryDetails(item) {
      this.selectedHistory = item
      this.historyDialog = true
    },
    formatCryptoAmount,
    historyLabel(item) {
      return getCryptoCategoryLabel(item.category, item.type === 'credit')
    },
    updateHistoryPage(page) {
      this.historyPage = page
      this.getWalletHistory()
    },
    updateHistoryPerPage(count) {
      this.historyPerPage = count
      this.historyPage = 1
      this.getWalletHistory()
    },
    async refreshCoin() {
      await this.getCoinDetail()
      this.getWalletHistory()
    },
    copy(text) {
      navigator.clipboard.writeText(text)
      this.$store.dispatch('alert', {
        message: 'Reference copied to clipboard',
        error: false,
        status: true
      })
    },
    async getCoinDetail() {
      this.loadingCoin = true
      try {
        const res = await cryptocoin.cryptoCoin().getCryptosWallets()
        this.wallets = res.data.data
        const wallet = res.data.data.find(
          (item) => item.coin._id === this.coinId
        )
        this.presentCoinData = wallet
        if (wallet?._id) {
          const fresh = await cryptocoin
            .cryptoCoin()
            .getCryptoWallet(wallet._id)
          if (!fresh.error && fresh.data?.data) {
            this.presentCoinData = fresh.data.data
          }
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingCoin = false
      }
    },
    async getCoinGraph() {
      this.loadingChart = true
      try {
        const res = await cryptocoin.cryptoCoin().getCryptosGraph(this.coinId)
        this.chart = Array.isArray(res.data.data) ? res.data.data : []
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingChart = false
      }
    },
    async getWalletHistory() {
      this.loadingHistory = true
      try {
        const res = await cryptocoin
          .cryptoCoin()
          .getCryptoWalletHistory(this.coinId, this.presentCoinData?._id, {
            limit: this.historyPerPage,
            offset: (this.historyPage - 1) * this.historyPerPage
          })
        // The endpoint answers { data: { data: [...], meta } } - reading
        // res.data.data as an array left this list permanently empty.
        const payload = res.data.data
        this.walletHistory = Array.isArray(payload)
          ? payload
          : (payload?.data ?? [])
        this.historyTotal = payload?.meta?.total ?? this.walletHistory.length
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingHistory = false
      }
    },
    async getCryptoAssets() {
      try {
        this.loadingAssets = true
        const res = await cryptocoin.cryptoCoin().getCryptosRate()
        this.cryptoAssets = res.data.data.map((asset) => ({
          symbol: asset.symbol,
          name: asset.currency,
          logo: asset.logo,
          price: asset.price,
          percentageChange: asset.percentageChange,
          coinId: asset.coinId
        }))
      } catch (error) {
        console.log('Error fetching crypto assets:', error)
      } finally {
        this.loadingAssets = false
      }
    },
    selectAsset(asset, type) {
      this.selectedAsset = asset
      this.showDepositModal = false

      // The fiat entry (NGNX) has no coinId/hasAutoProcessing - it isn't a
      // real swap target (there's no "swap crypto into Naira" endpoint,
      // that's the separate Sell flow). Route to the dedicated Naira-
      // purchase entry instead of falling through to the coinId-based
      // branches below, which would build a broken `/trade/undefined` URL.
      if (this.activeAction === 'swap' && type === 'fiat') {
        this.$router.push('/user/products/crypto/auto-trade/ngnx/swap?from=ngnx')
        return
      }

      if (asset.hasAutoProcessing) {
        this.$router.push(
          `/user/products/crypto/auto-trade/${asset.coinId}/${this.activeAction}`
        )
      } else {
        this.$router.push(`/user/products/crypto/trade/${asset.coinId}`)
      }
    },
    // Buy opens the swap screen as naira -> this coin, sell as this coin ->
    // naira; the screen reads both sides off the query.
    goToTrade(action) {
      // TEMPORARY: see utils/tradeRestriction.js
      if (isTradeRestricted(this.userCountry)) {
        this.restrictedAction = action
        this.showServiceUnavailable = true
        return
      }

      const path =
        action === 'buy'
          ? `/user/products/crypto/auto-trade/ngnx/swap?from=ngnx&to=${this.coinId}`
          : `/user/products/crypto/auto-trade/${this.coinId}/swap?to=ngnx`
      this.$router.push(path)
    },
    handleDepositModal(action) {
      // TEMPORARY: see utils/tradeRestriction.js
      if (isTradeRestricted(this.userCountry)) {
        this.restrictedAction = action
        this.showServiceUnavailable = true
        return
      }

      if (action === 'deposit' || action === 'withdraw') {
        this.$router.push(`/user/products/crypto/auto-trade/${this.coinId}/${action}`)
        return
      }

      this.activeAction = action
      this.showDepositModal = true
    }
  }
}
</script>

<style scoped>
.text-primary {
  color: #1200ff;
}
.border-primary {
  border-color: #1200ff !important;
}
.min-h-\[300px\] {
  min-height: 300px;
}
</style>
